import React, { useState, useEffect } from 'react'
import Footer from '../components/footer'
import Navbar from '../components/navbar'
import {
  PAYMENT_PLATFORMS,
  PaymentPlatform,
} from '../types/donation'
import { transactionsApi, TransactionBreakdown, AnalyticsFilter, DonationTypeSummary, GiftAidStats } from '../services/transactions-api'
import { donationsApi, DonationType } from '../services/donations-api'

// Helper to format currency (amount is in pence)
const formatCurrency = (amountInPence: number): string => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amountInPence / 100)
}

// Helper to get donation type name by ID
const getDonationTypeName = (id: string, donationTypes: DonationType[]): string => {
  return donationTypes.find(dt => dt.id === id)?.name || id
}

// Helper to get platform info
const getPlatformName = (platformId: string): string => {
  return PAYMENT_PLATFORMS.find(p => p.id === platformId)?.name || platformId
}

const getPlatformProvider = (platformId: string): string => {
  return PAYMENT_PLATFORMS.find(p => p.id === platformId)?.provider || ''
}

// Helper to format date for display
const formatDateRange = (startDate: string, endDate: string): string => {
  const start = new Date(startDate)
  const end = new Date(endDate)
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  return `${start.toLocaleDateString('en-GB', opts)} - ${end.toLocaleDateString('en-GB', opts)}`
}

// Platform colors for display
const platformColorsMap: Record<string, { bg: string; bar: string }> = {
  apple_pay: { bg: 'bg-gray-50', bar: 'bg-gray-800' },
  google_pay: { bg: 'bg-blue-50', bar: 'bg-blue-500' },
  paypal: { bg: 'bg-indigo-50', bar: 'bg-indigo-500' },
  bank_transfer: { bg: 'bg-green-50', bar: 'bg-green-500' },
  stripe: { bg: 'bg-blue-50', bar: 'bg-blue-500' },
}

type AnalyticsPeriod = 'all_time' | 'this_month' | 'last_month' | 'last_3_months' | 'last_6_months' | 'last_12_months' | 'custom'

const ANALYTICS_PERIOD_OPTIONS: { value: AnalyticsPeriod; label: string }[] = [
  { value: 'all_time', label: 'All Time' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'last_3_months', label: 'Last 3 Months' },
  { value: 'last_6_months', label: 'Last 6 Months' },
  { value: 'last_12_months', label: 'Last 12 Months' },
  { value: 'custom', label: 'Custom' },
]

// Helper to calculate date range for period
const getDateRangeForPeriod = (period: AnalyticsPeriod): { startDate?: string; endDate?: string } => {
  const now = new Date()
  const today = now.toISOString().split('T')[0]

  switch (period) {
    case 'all_time':
      return {} // No filter = all time
    case 'this_month': {
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
      return { startDate: firstDay.toISOString().split('T')[0], endDate: today }
    }
    case 'last_month': {
      const firstDayLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1)
      const lastDayLastMonth = new Date(now.getFullYear(), now.getMonth(), 0)
      return {
        startDate: firstDayLastMonth.toISOString().split('T')[0],
        endDate: lastDayLastMonth.toISOString().split('T')[0],
      }
    }
    case 'last_3_months': {
      const threeMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 3, 1)
      return { startDate: threeMonthsAgo.toISOString().split('T')[0], endDate: today }
    }
    case 'last_6_months': {
      const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1)
      return { startDate: sixMonthsAgo.toISOString().split('T')[0], endDate: today }
    }
    case 'last_12_months': {
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 12, 1)
      return { startDate: twelveMonthsAgo.toISOString().split('T')[0], endDate: today }
    }
    default:
      return {}
  }
}

// Convert TransactionBreakdown to display format
interface DonationTypeDisplay {
  donationTypeId: string
  amount: number
  count: number
  byPlatform: { platform: PaymentPlatform; amount: number; count: number }[]
}

interface DisplayAnalytics {
  totalAmount: number
  totalTransactions: number
  byDonationType: DonationTypeDisplay[]
  byPlatform: { platform: PaymentPlatform; amount: number; count: number }[]
  giftAid: GiftAidStats
}

const convertBreakdownToDisplay = (breakdown: TransactionBreakdown): DisplayAnalytics => {
  const byDonationType = Object.entries(breakdown.byDonationType).map(([id, summary]: [string, DonationTypeSummary]) => ({
    donationTypeId: id,
    amount: summary.totalAmount,
    count: summary.totalTransactions,
    byPlatform: Object.entries(summary.byPlatform || {}).map(([platform, pSummary]) => ({
      platform: platform as PaymentPlatform,
      amount: pSummary.totalAmount,
      count: pSummary.totalTransactions,
    })),
  }))

  const byPlatform = Object.entries(breakdown.byPlatform).map(([platform, summary]) => ({
    platform: platform as PaymentPlatform,
    amount: summary.totalAmount,
    count: summary.totalTransactions,
  }))

  return {
    totalAmount: breakdown.total.totalAmount,
    totalTransactions: breakdown.total.totalTransactions,
    byDonationType,
    byPlatform,
    giftAid: breakdown.giftAid,
  }
}

const AnalyticsPage: React.FC = () => {
  const [donationTypes, setDonationTypes] = useState<DonationType[]>([])
  const [analytics, setAnalytics] = useState<DisplayAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [selectedPeriod, setSelectedPeriod] = useState<AnalyticsPeriod>('all_time')
  const [activeTab, setActiveTab] = useState<'type' | 'platform'>('type')
  const [showDatePicker, setShowDatePicker] = useState(false)
  const [customStartDate, setCustomStartDate] = useState('')
  const [customEndDate, setCustomEndDate] = useState('')
  const [appliedCustomRange, setAppliedCustomRange] = useState<{ start: string; end: string } | null>(null)
  const [expandedDonationType, setExpandedDonationType] = useState<string | null>(null)

  // Fetch donation types on mount
  useEffect(() => {
    const fetchDonationTypes = async () => {
      try {
        const types = await donationsApi.listDonationTypes()
        setDonationTypes(types)
      } catch (err) {
        console.error('Failed to fetch donation types:', err)
      }
    }
    fetchDonationTypes()
  }, [])

  // Fetch analytics when period changes
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true)
      setError(null)

      try {
        let filter: AnalyticsFilter = {}

        if (selectedPeriod === 'custom' && appliedCustomRange) {
          filter = {
            startDate: appliedCustomRange.start,
            endDate: appliedCustomRange.end,
          }
        } else if (selectedPeriod !== 'custom') {
          const dateRange = getDateRangeForPeriod(selectedPeriod)
          filter = {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
          }
        } else {
          // Custom period selected but no range applied yet
          setLoading(false)
          return
        }

        const breakdown = await transactionsApi.getAnalytics(filter)
        setAnalytics(convertBreakdownToDisplay(breakdown))
      } catch (err) {
        console.error('Failed to fetch analytics:', err)
        setError('Failed to load analytics data. Please try again.')
      } finally {
        setLoading(false)
      }
    }

    fetchAnalytics()
  }, [selectedPeriod, appliedCustomRange])

  const handlePeriodChange = (period: AnalyticsPeriod) => {
    setSelectedPeriod(period)
    if (period === 'custom') {
      setShowDatePicker(true)
    } else {
      setShowDatePicker(false)
      setAppliedCustomRange(null)
    }
  }

  const handleApplyCustomRange = () => {
    if (customStartDate && customEndDate) {
      setAppliedCustomRange({ start: customStartDate, end: customEndDate })
      setShowDatePicker(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col h-screen">
        <Navbar />
        <div className="md:container mx-auto flex-1 py-8 px-4 overflow-auto">
          <div className="w-full max-w-4xl mx-auto">
            <div className="mb-6">
              <h1 className="text-xl font-bold text-gray-800">Donation Analytics</h1>
              <p className="text-gray-600 text-sm mt-1">
                View donation statistics across all platforms and donation types
              </p>
            </div>
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
                <span className="ml-3 text-gray-600">Loading analytics...</span>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col h-screen">
        <Navbar />
        <div className="md:container mx-auto flex-1 py-8 px-4 overflow-auto">
          <div className="w-full max-w-4xl mx-auto">
            <div className="mb-6">
              <h1 className="text-xl font-bold text-gray-800">Donation Analytics</h1>
            </div>
            <div className="bg-red-50 rounded-lg border border-red-200 p-6 text-center">
              <p className="text-red-800">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  // No data state
  if (!analytics) {
    return (
      <div className="flex flex-col h-screen">
        <Navbar />
        <div className="md:container mx-auto flex-1 py-8 px-4 overflow-auto">
          <div className="w-full max-w-4xl mx-auto">
            <div className="text-center py-6 text-gray-500 text-sm">
              No analytics data available.
            </div>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  const maxDonationTypeAmount = analytics.byDonationType.length > 0
    ? Math.max(...analytics.byDonationType.map(d => d.amount))
    : 0
  const maxPlatformAmount = analytics.byPlatform.length > 0
    ? Math.max(...analytics.byPlatform.map(p => p.amount))
    : 0

  return (
    <div className="flex flex-col h-screen">
      <Navbar />
      <div className="md:container mx-auto flex-1 py-8 px-4 overflow-auto">
        <div className="w-full max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <h1 className="text-xl font-bold text-gray-800">Donation Analytics</h1>
            <p className="text-gray-600 text-sm mt-1">
              View donation statistics across all platforms and donation types
            </p>
          </div>

          {/* Analytics Panel */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex flex-col gap-4 mb-6">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-800">Overview</h2>
                {/* Show current date range for custom period */}
                {selectedPeriod === 'custom' && appliedCustomRange && (
                  <span className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-lg">
                    {formatDateRange(appliedCustomRange.start, appliedCustomRange.end)}
                  </span>
                )}
              </div>

              {/* Time Period Filter */}
              <div className="flex flex-wrap gap-2">
                {ANALYTICS_PERIOD_OPTIONS.map(period => (
                  <button
                    key={period.value}
                    onClick={() => handlePeriodChange(period.value)}
                    className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                      selectedPeriod === period.value
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {period.label}
                  </button>
                ))}
              </div>

              {/* Custom Date Range Picker */}
              {showDatePicker && (
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="flex flex-wrap items-end gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Start Date</label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">End Date</label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <button
                      onClick={handleApplyCustomRange}
                      disabled={!customStartDate || !customEndDate}
                      className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      Apply
                    </button>
                    <button
                      onClick={() => {
                        setShowDatePicker(false)
                        if (!appliedCustomRange) {
                          setSelectedPeriod('all_time')
                        }
                      }}
                      className="px-4 py-2 text-gray-600 text-sm hover:text-gray-800"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {/* Show message when custom period selected but no range applied */}
              {selectedPeriod === 'custom' && !appliedCustomRange && !showDatePicker && (
                <button
                  onClick={() => setShowDatePicker(true)}
                  className="text-sm text-teal-600 hover:text-teal-700 text-left"
                >
                  Click to select date range...
                </button>
              )}
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg p-4 text-white">
                <div className="text-2xl md:text-3xl font-bold">
                  {formatCurrency(analytics.totalAmount)}
                </div>
                <div className="text-teal-100 text-sm mt-1">Total Donations</div>
              </div>
              <div className="bg-gradient-to-br from-gray-700 to-gray-800 rounded-lg p-4 text-white">
                <div className="text-2xl md:text-3xl font-bold">
                  {analytics.totalTransactions.toLocaleString()}
                </div>
                <div className="text-gray-300 text-sm mt-1">Transactions</div>
              </div>
              <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-lg p-4 text-white">
                <div className="text-2xl md:text-3xl font-bold">
                  {formatCurrency(analytics.giftAid.claimableAmount)}
                </div>
                <div className="text-emerald-100 text-sm mt-1">
                  Gift Aid Claimable
                </div>
                <div className="text-emerald-200 text-xs mt-0.5">
                  {analytics.giftAid.eligibleTransactions} eligible ({formatCurrency(analytics.giftAid.eligibleAmount)})
                </div>
              </div>
            </div>

            {/* Tab Buttons */}
            <div className="flex border-b border-gray-200 mb-5">
              <button
                onClick={() => setActiveTab('type')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'type'
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                By Donation Type
              </button>
              <button
                onClick={() => setActiveTab('platform')}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === 'platform'
                    ? 'border-teal-600 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                By Platform
              </button>
            </div>

            {/* By Donation Type */}
            {activeTab === 'type' && (
              <div className="space-y-3">
                {analytics.byDonationType.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No donation data for this period
                  </div>
                ) : (
                  analytics.byDonationType
                    .sort((a, b) => b.amount - a.amount)
                    .map(item => {
                      const percentage = maxDonationTypeAmount > 0 ? (item.amount / maxDonationTypeAmount) * 100 : 0
                      const totalPercentage = analytics.totalAmount > 0
                        ? ((item.amount / analytics.totalAmount) * 100).toFixed(1)
                        : '0'
                      const isExpanded = expandedDonationType === item.donationTypeId
                      const hasPlatformBreakdown = item.byPlatform && item.byPlatform.length > 0
                      const maxPlatformAmountForType = hasPlatformBreakdown
                        ? Math.max(...item.byPlatform.map(p => p.amount))
                        : 0

                      return (
                        <div key={item.donationTypeId} className="bg-gray-50 rounded-lg overflow-hidden">
                          <div
                            className={`p-4 ${hasPlatformBreakdown ? 'cursor-pointer hover:bg-gray-100 transition-colors' : ''}`}
                            onClick={() => {
                              if (hasPlatformBreakdown) {
                                setExpandedDonationType(isExpanded ? null : item.donationTypeId)
                              }
                            }}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-2">
                                {hasPlatformBreakdown && (
                                  <svg
                                    className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                  </svg>
                                )}
                                <span className="font-medium text-gray-800">
                                  {getDonationTypeName(item.donationTypeId, donationTypes)}
                                </span>
                                <span className="text-xs text-gray-500">
                                  ({totalPercentage}%)
                                </span>
                              </div>
                              <span className="font-bold text-gray-900">{formatCurrency(item.amount)}</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3 mb-1">
                              <div
                                className="bg-teal-500 h-3 rounded-full transition-all duration-300"
                                style={{ width: `${percentage}%` }}
                              />
                            </div>
                            <div className="text-xs text-gray-500">{item.count} transactions</div>
                          </div>

                          {/* Expanded Platform Breakdown */}
                          {isExpanded && hasPlatformBreakdown && (
                            <div className="border-t border-gray-200 bg-white p-4 space-y-2">
                              <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-3">
                                Platform Breakdown
                              </div>
                              {item.byPlatform
                                .sort((a, b) => b.amount - a.amount)
                                .map(platformItem => {
                                  const colors = platformColorsMap[platformItem.platform] || { bg: 'bg-gray-50', bar: 'bg-gray-500' }
                                  const platformPercentage = maxPlatformAmountForType > 0
                                    ? (platformItem.amount / maxPlatformAmountForType) * 100
                                    : 0
                                  const sharePercentage = item.amount > 0
                                    ? ((platformItem.amount / item.amount) * 100).toFixed(1)
                                    : '0'

                                  return (
                                    <div key={platformItem.platform} className={`${colors.bg} rounded-lg p-3`}>
                                      <div className="flex items-center justify-between mb-1.5">
                                        <div className="flex items-center gap-2">
                                          <span className="text-sm font-medium text-gray-700">
                                            {getPlatformName(platformItem.platform)}
                                          </span>
                                          <span className="text-xs text-gray-500">
                                            ({sharePercentage}%)
                                          </span>
                                        </div>
                                        <span className="text-sm font-bold text-gray-800">
                                          {formatCurrency(platformItem.amount)}
                                        </span>
                                      </div>
                                      <div className="w-full bg-gray-200 rounded-full h-2 mb-1">
                                        <div
                                          className={`${colors.bar} h-2 rounded-full transition-all duration-300`}
                                          style={{ width: `${platformPercentage}%` }}
                                        />
                                      </div>
                                      <div className="text-xs text-gray-500">{platformItem.count} transactions</div>
                                    </div>
                                  )
                                })}
                            </div>
                          )}
                        </div>
                      )
                    })
                )}
              </div>
            )}

            {/* By Platform */}
            {activeTab === 'platform' && (
              <div className="space-y-3">
                {analytics.byPlatform.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    No donation data for this period
                  </div>
                ) : (
                  analytics.byPlatform
                    .sort((a, b) => b.amount - a.amount)
                    .map(item => {
                      const percentage = maxPlatformAmount > 0 ? (item.amount / maxPlatformAmount) * 100 : 0
                      const totalPercentage = analytics.totalAmount > 0
                        ? ((item.amount / analytics.totalAmount) * 100).toFixed(1)
                        : '0'
                      const colors = platformColorsMap[item.platform] || { bg: 'bg-gray-50', bar: 'bg-gray-500' }

                      return (
                        <div key={item.platform} className={`${colors.bg} rounded-lg p-4`}>
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <span className="font-medium text-gray-800">
                                {getPlatformName(item.platform)}
                              </span>
                              <span className="text-xs text-gray-500 ml-2">
                                ({getPlatformProvider(item.platform)})
                              </span>
                              <span className="text-xs text-gray-500 ml-1">
                                • {totalPercentage}%
                              </span>
                            </div>
                            <span className="font-bold text-gray-900">{formatCurrency(item.amount)}</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-3 mb-1">
                            <div
                              className={`${colors.bar} h-3 rounded-full transition-all duration-300`}
                              style={{ width: `${percentage}%` }}
                            />
                          </div>
                          <div className="text-xs text-gray-500">{item.count} transactions</div>
                        </div>
                      )
                    })
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  )
}

export default AnalyticsPage
